/*
 * Copyright 2020 Thomer Gil [https://thomer.com/games/brickbreaker-js/]
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
// {{{ imports
import * as ex from 'excalibur';
import { Game } from './game';
import { Brick } from './brick';
import { BB } from './globals';
// }}}

export class Ball extends ex.Actor {
    // {{{ members, constructors
    private _velOnRelease: ex.Vector;

    constructor(loc: ex.Vector, vMult: number) {
        super({
            pos: new ex.Vector(loc.x, loc.y),
            color: BB.BALL_COLOR,
            radius: BB.BALL_RADIUS,
            collisionType: ex.CollisionType.Passive,
        });
        this._velOnRelease = new ex.Vector(0, 0);

        //
        // decide left/direction randomly. also, note that the ball can't
        // ever go faster than 60% of the brick or paddle height (which
        // ever is thinner). this is to prevent a whole bunch of problems
        // where the ball "flies through" the paddle or through bricks.
        //
        this._velOnRelease.x = BB.BALL_X_SPEED_SLOW;
        if (!Math.floor(Math.random() * 2)) {
            this._velOnRelease.x *= -1;
        }

        const initSpeed = BB.BALL_Y_SPEED * vMult;
        const minBrickPaddle = Math.min(BB.BRICK_HEIGHT, BB.PADDLE_HEIGHT);
        this._velOnRelease.y = Math.min(initSpeed, 0.6 * minBrickPaddle);

        // bounce against ...
        this.on('precollision', (ev) => {
            // In Excalibur 0.30, ev.other is the collider, get owner actor
            const otherActor = ev.other.owner;
            // ...brick
            if (otherActor instanceof Brick) {
                const intersection = ev.intersection.normalize();
                if (Math.abs(intersection.x) > Math.abs(intersection.y)) {
                    this.hbounce();
                } else {
                    this.vbounce();
                }
            }
        });
    }
    // }}}

    // {{{ public
    public update(engine, delta): void {
        super.update(engine, delta);

        // left side bounce
        if (this.getx() <= BB.BALL_RADIUS) {
            // fix weird behavior where it would get stuck beyond the edge
            this.pos.x = BB.BALL_RADIUS;
            this.hbounce();
        }

        // right side bounce
        if (this.getx() + BB.BALL_RADIUS >= Game.Instance().drawWidth) {
            // fix weird behavior where it would get stuck beyond the edge
            this.pos.x = Game.Instance().drawWidth - BB.BALL_RADIUS;
            this.hbounce();
        }

        // top side bounce
        if (this.gety() <= BB.BALL_RADIUS) {
            this.pos.y = BB.BALL_RADIUS;
            this.vbounce();
        }

        // ball is lost
        if (this.gety() + BB.BALL_RADIUS > Game.Instance().drawHeight - BB.BELOW_PADDLE_EMPTY_SPACE) {
            Game.Instance().loseBall(this);
        }
    }

    public getx(): number {
        return this.pos.x;
    }

    public gety(): number {
        return this.pos.y;
    }

    public xvelocity(): number {
        return this.vel.x;
    }

    public yvelocity(): number {
        return this.vel.y;
    }

    public setXVelocity(v): void {
        this.vel.x = v;
    }

    public setYVelocity(v): void {
        this.vel.y = v;
    }

    public hbounce(): void {
        this.vel.x *= -1;
    }

    public vbounce(): void {
        this.vel.y *= -1;
    }

    public rightToLeft(): boolean {
        return this.vel.x < 0;
    }

    // Called by Game after countdown to start the ball moving
    public release(): void {
        this.vel = this._velOnRelease;
    }
    // }}}
}
